import type { FunctionComponent } from 'react'
import AsyncResource from '../../../components/async-resource/async-resource'
import { Breadcrumb } from '../../../components/breadcrumb'
import { ActiveComponentSecrets } from '../../../components/component/secrets/ActiveComponentSecrets'
import { EnvironmentVariables } from '../../../components/environment-variables'
import { EventsList } from '../../../components/events-list'
import {
  activeComponentEnvVarsListExpandedKey,
  componentEventListExpandedKey,
  useLocalStorage,
} from '../../../hooks/use-local-storage'
import { routes } from '../../../router/routes'
import { pollingInterval } from '../../../store/defaults'
import { useGetApplicationQuery, useGetComponentEventsQuery, useGetEnvironmentQuery } from '../../../store/radix-api'
import { getEnvsUrl } from '../../../utils/routing'
import { dataSorter, sortCompareDate } from '../../../utils/sort-utils'
import { routeWithParams } from '../../../utils/string'
import { ActiveComponentToolbar } from './ActiveComponentToolbar'
import { ComponentReplicaList } from './ComponentReplicaList'
import { ComponentReplicaLogAccordion } from './ComponentReplicaLogAccordion'
import { ComponentStatus } from './ComponentStatus'
import { ComponentVulnerabilityDetails } from './ComponentVulnerabilityDetails'
import { ExternalDNSAccordion } from './ExternalDns'
import { HorizontalScalingSummary } from './HorizontalScalingSummary'
import { OAuthService } from './OauthService'
import { Overview } from './Overview'

import '../style.css'

export const ActiveComponentOverview: FunctionComponent<{
  appName: string
  envName: string
  componentName: string
}> = ({ appName, envName, componentName }) => {
  const { data: application } = useGetApplicationQuery({ appName }, { skip: !appName, pollingInterval })
  const {
    data: environment,
    refetch,
    ...envState
  } = useGetEnvironmentQuery({ appName, envName }, { skip: !appName || !envName, pollingInterval })

  const { appAlias } = application || {}
  const deployment = environment?.activeDeployment
  const component = deployment?.components?.find(({ name }) => name === componentName)

  const componentDNSAliases = application?.dnsAliases?.filter(
    (dnsAlias) => dnsAlias.componentName === componentName && dnsAlias.environmentName == envName
  )

  const [isEventListExpanded, setIsEventListExpanded] = useLocalStorage(componentEventListExpandedKey, false)
  const { data: events } = useGetComponentEventsQuery(
    { appName, envName, componentName },
    {
      skip: !appName || !envName || !componentName || !isEventListExpanded,
      pollingInterval,
    }
  )
  const [isEnvVarsListExpanded, setIsEnvVarsListExpanded] = useLocalStorage(activeComponentEnvVarsListExpandedKey, true)

  return (
    <>
      <Breadcrumb
        links={[
          { label: appName, to: routeWithParams(routes.app, { appName }) },
          { label: 'Environments', to: getEnvsUrl(appName) },
          {
            label: envName,
            to: routeWithParams(routes.appEnvironment, { appName, envName }),
          },
          { label: componentName },
        ]}
      />

      <AsyncResource asyncState={envState}>
        {component && (
          <>
            <ActiveComponentToolbar component={component} appName={appName} envName={envName} refetch={refetch} />

            <ComponentStatus
              appName={appName}
              envName={envName}
              componentName={componentName}
              refetch={refetch}
              component={component}
            />

            <Overview
              appName={appName}
              appAlias={appAlias}
              dnsAliases={componentDNSAliases}
              dnsExternalAliases={component.externalDNS}
              envName={envName}
              component={component}
              deployment={deployment}
            />

            <div className="grid grid--gap-large">
              <ComponentReplicaList
                showUtilization
                title={'Replicas'}
                appName={appName}
                envName={envName}
                componentName={componentName}
                replicaList={component.replicaList}
                isExpanded
              />
              <ComponentReplicaLogAccordion
                title={'Replica Logs'}
                appName={appName}
                envName={envName}
                componentName={componentName}
              />
              {component.oauth2 && (
                <OAuthService
                  appName={appName}
                  envName={envName}
                  componentName={componentName}
                  oauth2={component.oauth2}
                  refetch={refetch}
                />
              )}
              <ComponentVulnerabilityDetails appName={appName} envName={envName} componentName={componentName} />
              {component.externalDNS && component.externalDNS.length > 0 && (
                <ExternalDNSAccordion
                  appName={appName}
                  envName={envName}
                  componentName={componentName}
                  externalDNSList={component.externalDNS}
                />
              )}
              <ActiveComponentSecrets
                appName={appName}
                componentName={componentName}
                envName={envName}
                secretNames={component.secrets}
              />
              <EventsList
                isExpanded={isEventListExpanded}
                onExpanded={setIsEventListExpanded}
                events={dataSorter(events ?? [], [
                  ({ lastTimestamp: x }, { lastTimestamp: y }) => sortCompareDate(x, y, 'descending'),
                ])}
              />
              <EnvironmentVariables
                appName={appName}
                envName={envName}
                componentName={componentName}
                componentType={component.type}
                isExpanded={isEnvVarsListExpanded}
                onExpanded={setIsEnvVarsListExpanded}
              />
              {component.horizontalScalingSummary && (
                <HorizontalScalingSummary summary={component.horizontalScalingSummary} />
              )}
            </div>
          </>
        )}
      </AsyncResource>
    </>
  )
}
